import React, { Fragment } from "react";
import T from "prop-types";
import { Link } from "react-router-dom";
import _ from "lodash";
import moment from "moment";
import {
  CONDITION,
  INVENTORIES,
  SRP_CONDITION
} from "MODULES/inventories/constants";
import humanizeString from "humanize-string";
import { dollars } from "LIB/formatters/currency";
import { shortDateYYandTime } from "LIB/formatters/date";
import { DOM } from "MODULES/parts/constants";
import { truncate, getFirstNameLastInitial } from "LIB/formatters/text";
import lotSvg from "APP_ASSETS/images/lot.svg";
import PartLink from "MODULES/Notifications/components/Search/PartLink";
import ItemLink from "MODULES/Notifications/components/Search/ItemLink";
import InventoryLink from "MODULES/Notifications/components/Search/InventoryLink";
import { PARTIAL_CREDIT } from "MODULES/RepairOrders/constants";
import {
  restrictedAddressesToSentence,
  restrictedEntityToSentence
} from "MODULES/Notifications/helpers";

const toInventoryConditionLabel = key =>
  INVENTORIES.condition[key].label.toLowerCase();

const normalizeChangedKey = key => {
  if (key === DOM) {
    return String(key).toUpperCase();
  }

  return humanizeString(key);
};

const normalizeChangeValue = (key, value) => {
  if (key === DOM) {
    const dom = moment(value);

    if (dom.isValid()) {
      return dom.format("YYYY-MM");
    }
  }

  return value || "blank";
};

const CompanyLink = ({ company }) => (
  <Link to={`/companies/${company.id}`}>
    <span>{truncate(company.name, 20)}</span>
  </Link>
);

CompanyLink.propTypes = {
  company: T.shape({})
};

const getChangedDetails = notification => {
  const conditionKeys = [CONDITION, SRP_CONDITION];

  return Object.keys(notification.details.changes).map(key => {
    const fromValue = normalizeChangeValue(
      key,
      notification.details.changes[key][1]
    );
    const toValue = normalizeChangeValue(
      key,
      notification.details.changes[key][0]
    );

    if (conditionKeys.find(k => k === key) === undefined) {
      return (
        <span key={key}>
          <br />
          {normalizeChangedKey(key)} changed from {fromValue} to {toValue}
        </span>
      );
    }

    return (
      <span key={key}>
        <br />
        {humanizeString(key)} changed from{" "}
        {fromValue && toInventoryConditionLabel(fromValue)} to{" "}
        {toValue && toInventoryConditionLabel(toValue)}
      </span>
    );
  });
};

const NOTIFICATION_TYPES = {
  Company: {
    company_approved_status: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => {
        const message = [];
        if (notification.details.vendor_status) {
          message.push("approved vendor");
        }

        if (notification.details.repair_station_status) {
          message.push("approved repair station");
        }

        return (
          <span>
            {_.get(notification, "details.name")} added to the{" "}
            {message.join(" / ")} list.
          </span>
        );
      }
    },
    no_recent_sale_for_end_user_company: {
      icon: "fa-dollar",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.name} hasn&apos;t purchased a part in 90 days.
        </span>
      )
    },
    outgoing_quote_terms_not_exist: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.details.contact.name} at {notification.notifier.name},
          has requested a quote for the first time. Please consider requesting
          credit references from{" "}
          <a href={`mailto:${notification.details.contact.email}`}>
            {notification.details.contact.email}
          </a>
          .
        </span>
      )
    },
    credit_references_updated: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Credit References have been uploaded for your review for Company --{" "}
          <strong>{notification.notifier.name}</strong>.
        </span>
      )
    },
    lot_missing_quickbooks_company: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.details.lot.uuid}
            path="lots"
            id={notification.details.lot.uuid}
            displayText={notification.details.lot.code}
          />{" "}
          was created for {notification.notifier.name} which has no matching
          name in Accounting.
        </span>
      )
    },
    repairs_missing_quickbooks_company: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.details.repair_order.uuid}
            path="repair-orders"
            id={notification.details.repair_order.uuid}
            displayText={notification.details.repair_order.code}
          />{" "}
          was created for {notification.notifier.name} which has no matching
          name in Accounting.
        </span>
      )
    },
    missing_accounting_vendor_integration: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            id={notification.details.purchase_order.uuid}
            path="purchase-orders"
            displayText={notification.details.purchase_order.code}
          />{" "}
          was created for {notification.notifier.name} which has no matching
          name in accounting.
        </span>
      )
    },
    missing_quickbooks_company: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.details.sales_order.uuid}
            path="sales-orders"
            id={notification.details.sales_order.uuid}
            displayText={notification.details.sales_order.code}
          />{" "}
          was created for {notification.notifier.name} which has no matching
          name in Accounting.
        </span>
      )
    },
    vendor_approval_request: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.created_by.full_name} requested vendor approval for{" "}
          <ItemLink
            notificationId={notification.id}
            id={notification.notifier.uuid}
            path="companies"
            displayText={notification.notifier.name}
          />
          .
        </span>
      )
    },
    vendor_approval_request_reminder: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          One week since vendor approval requested for{" "}
          <ItemLink
            notificationId={notification.id}
            id={notification.notifier.uuid}
            path="companies"
            displayText={notification.notifier.name}
          />
          .
        </span>
      )
    },
    repair_station_approval_request: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.created_by.full_name} requested repair station approval{" "}
          for {notification.notifier.name}.
        </span>
      )
    },
    repair_station_approval_request_reminder: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          One week since repair station approval was requested for{" "}
          {notification.notifier.name}.
        </span>
      )
    },
    vendor_approval_status_change: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.created_by.full_name} updated vendor approval status for{" "}
          {notification.notifier.name}.
        </span>
      )
    },
    repair_station_approval_status_change: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.created_by.full_name} updated repair station approval
          status for {notification.notifier.name}.
        </span>
      )
    },
    qc_export_compliance_reminder: {
      icon: "fa-building",
      url: notification => `/companies/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          One week since request for export compliance for{" "}
          {notification.notifier.name}.
        </span>
      )
    }
  },
  Consignment: {
    consignment_import_failed: {
      icon: "fa-handshake-o",
      url: notification => `/consignments/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Consignment Import Failed.{" "}
          {_.get(notification, "details.processing_error")
            ? "Processing error please check file type."
            : ""}
          .
        </span>
      )
    },
    consignment_import_succeeded: {
      icon: "fa-handshake-o",
      url: notification => `/consignments/${notification.notifier.uuid}`,
      message: () => <span>Consignment Import Succeeded.</span>
    }
  },
  Inventory: {
    inventory_condition_does_not_match_srp: {
      iconUrl: "https://aero-zone-assets.s3.amazonaws.com/inventory.png",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink part={notification.notifier.part} /> condition does not
          match the inventory SRP condition.
        </span>
      )
    },
    lot_repair_order: {
      icon: "fa-cogs",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            part={notification.details.part}
            partId={notification.details.part.id}
            partNumber={notification.details.part.part_number}
          />{" "}
          /{" "}
          <InventoryLink
            notificationId={notification.id}
            id={notification.details.inventory.id}
            serialNumber={notification.details.inventory.serial_number}
          />
          requires SRP Price & Condition.
        </span>
      )
    },
    beyond_econ_repair_condition_change: {
      iconUrl: "https://aero-zone-assets.s3.amazonaws.com/inventory.png",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
          />{" "}
          SN-{notification.notifier.serial_number} condition updated from{" "}
          {toInventoryConditionLabel(
            _.get(notification, "details.previous_condition")
          )}
          . Please relocate to applicable stock location.
        </span>
      )
    },
    ema_reserved_for_sale: {
      icon: "fa-dollar",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          EMA{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
          />{" "}
          reserved for sale. Please request QC documents (if applicable).
        </span>
      )
    },
    inventory_expiration: {
      icon: "fa-cogs",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
          />{" "}
          SN-{notification.notifier.serial_number} is set to expire at the end
          of next month. Please consider for repair or segregation.
        </span>
      )
    },
    inventory_added_to_reserve_list: {
      icon: "fa-dollar",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: () => <span>Inventory added to Sales Cart.</span>
    },
    notify_po_received_with_existing_so: {
      icon: "fa-shopping-cart",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
          />{" "}
          arrived into {notification.details.store_location} stock today tied to{" "}
          {notification.details.sales_order.code}. Please prepare QC documents.
        </span>
      )
    },
    request_scrap_at_shop_reminder: {
      icon: "fa-wrench",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Still awaiting confirmation of scrap at shop from{" "}
          {notification.details.repair_order.company.name} for PN{" "}
          {notification.notifier.part.part_number} SN{" "}
          {notification.notifier.serial_number}.
        </span>
      )
    },
    srp_below_combined_cost_and_repair_cost: {
      icon: "fa-cogs",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          SRP Warning: SRP dropped below cost plus estimated repair for{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part.id}
          />{" "}
          SN-{_.get(notification, "notifier.serial_number")}.
        </span>
      )
    },
    verify_repair_station_approval_status: {
      icon: "fa-truck",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => {
        const company = _.get(
          notification,
          "details.tagged_by",
          _.get(notification, "company.name")
        );
        return (
          <span>
            <PartLink part={notification.notifier.part} /> received with
            approval certs by&nbsp;
            {company}. Please verify approval status for {company}.
          </span>
        );
      }
    },
    reserved_po_received_and_passes_qc: {
      icon: "fa-wrench",
      url: () => "/repair-orders/reserved",
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
          />{" "}
          from {notification.details.purchase_order_code} passes QC inspection,
          please proceed to create RO.
        </span>
      )
    },
    exchange_received_and_passes_qc: {
      icon: "fa-wrench",
      url: () => "/repair-orders/reserved",
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
          />{" "}
          from{" "}
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            displayText={`SO-${notification.details.sales_order.code}`}
            id={notification.details.sales_order.uuid}
          />{" "}
          passes QC inspection, please proceed to create RO.
        </span>
      )
    },
    inventory_condition_change_to_unsellable: {
      iconUrl: "https://aero-zone-assets.s3.amazonaws.com/inventory.png",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <div>
          <span>
            Condition for{" "}
            <PartLink
              notificationId={notification.id}
              part={_.get(notification, "notifier.part")}
            />{" "}
            SN-{_.get(notification, "notifier.serial_number")} changed to{" "}
            {toInventoryConditionLabel(notification.notifier.condition)}. Please
            relocate to applicable stock location.
          </span>
        </div>
      )
    },
    reserved_po_with_mod_to_received_and_passes_qc: {
      icon: "fa-wrench",
      url: () => "/repair-orders/reserved",
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
          />{" "}
          from {_.get(notification, "details.purchase_order_code")} passes QC
          inspection. Please proceed to create RO for mod to{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.mod_to")}
          />{" "}
          from{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
          />
          .
        </span>
      )
    },
    ro_received_without_mod: {
      icon: "fa-wrench",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Repair Order received without modification to PN:{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.mod_to")}
          />
          .
        </span>
      )
    },
    qc_status: {
      icon: "fa-shopping-cart",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          An item from {notification.details.purchase_order_code}, for which you
          were a Buyer, has been escalated to QC Hold.
        </span>
      )
    },
    sales_order_for_active_repair_cancelled: {
      icon: "fa-dollar",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            displayText={notification.details.sales_order.code}
            id={
              notification.details.sales_order.id ||
              notification.details.sales_order.uuid
            }
          />{" "}
          cancelled. Please review{" "}
          <PartLink
            part={_.get(notification, "notifier.part")}
            notificationId={notification.id}
            partNumber={_.get(notification, "notifier.part.part_number")}
            partId={notification.notifier.part_id}
          />{" "}
          SN-{_.get(notification, "notifier.serial_number")} from open{" "}
          <ItemLink
            notificationId={notification.id}
            path="repair-orders"
            displayText={notification.details.repair_order.code}
            id={
              notification.details.repair_order.id ||
              notification.details.repair_order.uuid
            }
          />
          .
        </span>
      )
    },
    sales_item_for_active_repair_cancelled: {
      icon: "fa-dollar",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partNumber={_.get(notification, "notifier.part.part_number")}
            partId={notification.notifier.part_id}
          />{" "}
          from{" "}
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            displayText={notification.details.sales_order.code}
            id={
              notification.details.sales_order.uuid ||
              notification.details.sales_order.id
            }
          />{" "}
          cancelled. Please review open{" "}
          <ItemLink
            notificationId={notification.id}
            path="repair-orders"
            displayText={notification.details.repair_order.code}
            id={
              notification.details.repair_order.uuid ||
              notification.details.repair_order.id
            }
          />
          .
        </span>
      )
    },
    write_up_an_internal_outright_sale: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          This exchange has been marked for an Internal Outright. Please create{" "}
          an Internal Outright Sale for this Core.{" "}
          <strong>
            Part:{" "}
            <PartLink
              notificationId={notification.id}
              part={_.get(notification, "notifier.part")}
              partNumber={_.get(notification, "notifier.part.part_number")}
              partId={notification.notifier.part_id}
            />{" "}
            - SN: - SN: {notification.notifier.serial_number}
          </strong>
          .
        </span>
      )
    },
    write_up_an_exchange_gone_outright_sale: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.details.sales_order.id}`,
      message: notification => (
        <span>
          This exchange has Gone Outright. Please create an Outright Sale for
          this part.{" "}
          <strong>
            <PartLink
              notificationId={notification.id}
              part={_.get(notification, "notifier.part")}
              partNumber={_.get(notification, "notifier.part.part_number")}
              partId={notification.notifier.part_id}
            />{" "}
            - SN: {notification.notifier.serial_number}
          </strong>
          .
        </span>
      )
    },
    outright_sale_being_written_for_part: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.details.sales_order.id}`,
      message: notification => (
        <span>
          Exchange Sale for{" "}
          <strong>{notification.details.sales_order.code}</strong> has gone
          outright!.
        </span>
      )
    },
    outright_sale_created: {
      icon: "fa-dollar",
      url: notification =>
        `/reports/commissions?filter=${notification.details.sales_order.code}`,
      message: notification => (
        <span>
          Congrats!{" "}
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            displayText={notification.details.sales_order.code}
            id={notification.details.sales_order.uuid}
          />{" "}
          created.
        </span>
      )
    },
    received_transfer: {
      iconUrl: "https://aero-zone-assets.s3.amazonaws.com/inventory.png",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partNumber={_.get(notification, "notifier.part.part_number")}
            partId={notification.notifier.part_id}
          />{" "}
          SN-{notification.notifier.serial_number} received from inventory
          transfer.
        </span>
      )
    },
    partially_received_transfer: {
      iconUrl: "https://aero-zone-assets.s3.amazonaws.com/inventory.png",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partNumber={_.get(notification, "notifier.part.part_number")}
            partId={notification.notifier.part_id}
          />{" "}
          SN-{notification.notifier.serial_number} partially received from
          inventory transfer.
        </span>
      )
    },
    sales_order_has_been_created_for_item_being_repaired: {
      icon: "fa-dollar",
      url: notification =>
        notification.details
          ? `/sales-orders/${notification.details.sales_order.id}`
          : `/inventories/${notification.notifier.uuid}`,
      message: notification => {
        const salesOrderCode = notification.details ? (
          <strong>({notification.details.sales_order.code}) </strong>
        ) : (
          ""
        );
        return (
          <span>
            A sales order {salesOrderCode}has been created for:{" "}
            <strong>
              <PartLink
                notificationId={notification.id}
                part={_.get(notification, "notifier.part")}
                partNumber={_.get(notification, "notifier.part.part_number")}
                partId={notification.notifier.part_id}
              />{" "}
              - SN: {notification.notifier.serial_number}
            </strong>{" "}
            which is being repaired.
          </span>
        );
      }
    },
    inventory_condition_change_to_scrap: {
      icon: "fa-cogs",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partNumber={_.get(notification, "notifier.part.part_number")}
            partId={notification.notifier.part_id}
          />{" "}
          SN-{_.get(notification, "notifier.serial_number")} scrapped and
          remaining cost depreciated.
        </span>
      )
    },
    inventory_srp_reduction: {
      icon: "fa-quote-right",
      url: notification => `/inventories/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          SRP reduction for{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partNumber={_.get(notification, "notifier.part.part_number")}
            partId={notification.notifier.part_id}
          />
          . Please follow up with your customer.
        </span>
      )
    }
  },
  Lot: {
    expect_paperwork_for_vendor: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} created to an unapproved vendor. Please
          review vendor status and trace.
        </span>
      )
    },
    denied_vendor_status: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} placed and {notification.details.company}{" "}
          has a denied vendor status. Please approve trace and reevaluate vendor
          status.
        </span>
      )
    },
    lot_cancelled: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.details && notification.details.buying_store ? (
            <Fragment>{notification.details.buying_store} </Fragment>
          ) : (
            ""
          )}
          {notification.notifier.code} cancelled by{" "}
          {notification.created_by.full_name}.
        </span>
      )
    },
    lot_cost_changed: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.name} from{" "}
          {notification.details && notification.details.buying_store ? (
            <Fragment>{notification.details.buying_store} </Fragment>
          ) : (
            ""
          )}
          {notification.notifier.code} to {notification.details.company_name}{" "}
          changed from {dollars(notification.details.previous_value)} to{" "}
          {dollars(notification.details.current_value)} by{" "}
          {notification.details.updated_by}.
        </span>
      )
    },
    lot_change_do_not_ship_reminder: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} is awaiting completed Ship To
          instructions.
        </span>
      )
    },
    must_receive_virtually: {
      iconUrl: lotSvg,
      url: () => `/dashboard/virtual-receiving`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            id={notification.notifier.uuid}
            displayText={notification.notifier.code}
            path="lots"
          />{" "}
          requires virtual receiving.
        </span>
      )
    },
    must_receive_virtually_updated: {
      iconUrl: lotSvg,
      url: () => `/dashboard/virtual-receiving`,
      message: notification => (
        <span>
          {notification.notifier.code} updated and requires virtual receiving.
        </span>
      )
    },
    import_failed: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.lot.uuid}`,
      message: notification => (
        <span>
          Lot Import Failed.{" "}
          {_.get(notification, "details.processing_error")
            ? "Processing error please check file type."
            : ""}
        </span>
      )
    },
    import_succeeded: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.lot.uuid}`,
      message: () => <span>Lot Import Succeeded.</span>
    },
    lot_trace_hold_approved: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Trace for {notification.notifier.code} approved. Please send purchase
          order to {_.get(notification, "details.company_name")}.
        </span>
      )
    },
    lot_trace_hold: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} to{" "}
          {_.get(notification, "details.company.name")} requires trace approval.
        </span>
      )
    },
    lot_trace_hold_rejected: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Trace for <strong>{notification.notifier.code}</strong> has been
          denied by {_.get(notification, "details.rejected_by")} and order to{" "}
          {_.get(notification, "details.company_name")} should be rejected.
        </span>
      )
    }
  },
  Location: {
    quickbooks_authentication_error: {
      icon: "fa-dollar",
      url: () => "/integrations",
      message: notification => (
        <span>
          Please reauthorize quickbooks for {notification.details.name} location
          and resync.
        </span>
      )
    }
  },
  LotLineItem: {
    difference_in_received_inventory_item: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.lot.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          received with these differences: {getChangedDetails(notification)}.
        </span>
      )
    },
    difference_in_partially_received_inventory_item: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.lot.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          partially received with these differences:
          <br />
          {getChangedDetails(notification)}.
        </span>
      )
    },
    first_lpo_part_received: {
      iconUrl: lotSvg,
      url: notification => `/lots/${notification.notifier.lot.uuid}`,
      message: notification => (
        <span>
          First part of {notification.notifier.lot.code} has been received.
        </span>
      )
    }
  },
  MistakeShipment: {
    mistake_shipment_missing_ship_to: {
      icon: "fa-exclamation-triangle",
      url: notification => `/mistake-shipments/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Mistake Shipment for{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partNumber={_.get(notification, "notifier.part.part_number")}
            partId={_.get(notification, "notifier.part.id")}
          />{" "}
          / SN {_.get(notification, "notifier.serial_number")} does not have a
          Shipping Address.
        </span>
      )
    }
  },
  OutgoingQuote: {
    restricted_entity: {
      icon: "fa-quote-left",
      url: notification => `/outgoing-quotes/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Warning: Similar entity name in US Gov&apos;t Screening List:{" "}
          {restrictedEntityToSentence(notification.details)}.
        </span>
      )
    }
  },
  OutgoingQuoteLineItem: {
    airline_rfq_received: {
      icon: "fa-quote-right",
      url: notification =>
        `/outgoing-quotes/${notification.notifier.outgoing_quote_id}`,
      message: notification => (
        <span>
          {_.get(notification, "details.outgoing_quote.code")} Airline RFQ
          received from {_.get(notification, "details.company.name")} for Stock{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partId={notification.notifier.part_id}
            partNumber={_.get(notification, "notifier.part.part_number")}
          />.
        </span>
      )
    },
    inventory_srp_reduction_below_offer: {
      icon: "fa-quote-right",
      url: notification =>
        `/outgoing-quotes/${notification.notifier.outgoing_quote_id}`,
      message: notification => (
        <span>
          SRP reduction for{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partId={notification.notifier.part_id}
            partNumber={_.get(notification, "notifier.part.part_number")}
          />{" "}
          is at or below offer made to you. Please follow up with your customer.
        </span>
      )
    },
    offer_made: {
      icon: "fa-quote-right",
      url: notification =>
        `/outgoing-quotes/${notification.notifier.outgoing_quote_id}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partId={notification.notifier.part_id}
            partNumber={_.get(notification, "notifier.part.part_number")}
          />{" "}
          received an offer of {dollars(notification.details.price)}.
        </span>
      )
    },
    outgoing_quote_follow_up_feedback_added: {
      icon: "fa-quote-right",
      url: notification =>
        `/outgoing-quotes/${notification.details.outgoing_quote.uuid}`,
      message: notification => (
        <span>
          Follow up feedback entered for{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.part")}
          />
          .
        </span>
      )
    },
    outgoing_quote_reminder: {
      icon: "fa-quote-right",
      url: notification =>
        `/outgoing-quotes/${notification.notifier.outgoing_quote_id}`,
      message: notification => (
        <span>
          Please follow up with your customer for
          <strong> OQ-{notification.notifier.outgoing_quote_id}</strong>.
        </span>
      )
    },
    quote_no_longer_sellable: {
      icon: "fa-quote-right",
      url: notification =>
        `/outgoing-quotes/${notification.notifier.outgoing_quote_id}`,
      message: notification => (
        <span>
          <PartLink part={_.get(notification, "notifier.part")} /> has been
          sold. You quoted it recently. Please review.
        </span>
      )
    },
    repeated_outgoing_quote: {
      icon: "fa-quote-right",
      url: notification =>
        `/parts/${notification.notifier.part_id}/outgoing-quote-line-items`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partId={notification.notifier.part_id}
            partNumber={_.get(notification, "notifier.part.part_number")}
          />{" "}
          quoted at least 3 times in 24 hours. Please follow up with your
          customer.
        </span>
      )
    },
    sellable_inventory_available_for_no_quote: {
      icon: "fa-cogs",
      url: notification =>
        `/parts/${notification.notifier.part_id}/outgoing-quote-line-items`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.part")}
            partNumber={_.get(notification, "details.part.part_number")}
            partId={notification.notifier.part_id}
          />{" "}
          now in stock. You recently No Quoted{" "}
          {_.get(notification, "details.company.name")}.
        </span>
      )
    }
  },
  Part: {
    automatic_srp_adjustment_anniv: {
      icon: "fa-cogs",
      url: notification => `/parts/${notification.notifier.uuid}`,
      message: notification => {
        const part = notification.notifier;

        const monthsAgo = _.get(notification, "details.months_ago");
        return (
          <span>
            SRP automatic adjustment: {monthsAgo}-month anniversary date for{" "}
            <PartLink
              notificationId={notification.id}
              part={part}
              partId={notification.notifier.uuid}
              partNumber={part.part_number}
            />
            .
          </span>
        );
      }
    },
    notify_missing_eccn_details: {
      icon: "fa-truck",
      url: notification => `/parts/${notification.details.part.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
            partNumber={notification.details.part.part_number}
            partId={notification.details.part.id}
          />{" "}
          received and missing ECCN details.
        </span>
      )
    },
    automatic_srp_adjustment_socs: {
      icon: "fa-cogs",
      url: notification => `/parts/${notification.notifier.uuid}`,
      message: notification => {
        const part = notification.notifier;

        return (
          <span>
            SRP automatic adjustment: An order for{" "}
            <PartLink
              notificationId={notification.id}
              part={part}
              partId={notification.notifier.uuid}
              partNumber={part.part_number}
            />{" "}
            may have been missed.
          </span>
        );
      }
    },
    automatic_srp_adjustment_maps: {
      icon: "fa-cogs",
      url: notification => `/parts/${notification.notifier.uuid}`,
      message: notification => {
        const part = notification.notifier;

        return (
          <span>
            SRP automatic adjustment:{" "}
            <PartLink
              notificationId={notification.id}
              part={part}
              partId={notification.notifier.uuid}
              partNumber={part.part_number}
            />{" "}
            has no outgoing quote activity.
          </span>
        );
      }
    },
    looking_for_quote_resident_expert: {
      icon: "fa-cogs",
      url: notification =>
        `/outgoing-quotes/${notification.details.outgoing_quote.uuid ||
          notification.details.outgoing_quote.id}`,
      message: notification => (
        <span>
          Resident Expert: {_.get(notification, "details.company.name")} is
          looking for{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier")}
            partId={notification.notifier.id}
            partNumber={_.get(notification, "notifier.part_number")}
          />.
        </span>
      )
    },
    no_quote_with_resident_expert: {
      icon: "fa-cogs",
      url: notification =>
        `/outgoing-quotes/${notification.details.outgoing_quote.uuid ||
          notification.details.outgoing_quote.id}`,
      message: notification => (
        <span>
          Resident Expert:{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier")}
            partId={notification.notifier.id}
            partNumber={_.get(notification, "notifier.part_number")}
          />{" "}
          was recently ‘No Quoted’ to{" "}
          {_.get(notification, "details.company.name")}.
        </span>
      )
    },
    missed_srp_adjustment_anniv: {
      icon: "fa-cogs",
      url: notification => `/parts/${notification.notifier.uuid}`,
      message: notification => {
        const part = notification.notifier;

        return (
          <span>
            Anniversary: PN{" "}
            <PartLink
              notificationId={notification.id}
              part={part}
              partId={notification.notifier.uuid}
              partNumber={part.part_number}
            />
            . Review recommended for possible SRP adjustment.
          </span>
        );
      }
    },
    missed_srp_adjustment_socs: {
      icon: "fa-cogs",
      url: notification => `/parts/${notification.notifier.uuid}`,
      message: notification => {
        const part = notification.notifier;

        return (
          <span>
            SRP automatic adjustment: An order for{" "}
            <PartLink
              notificationId={notification.id}
              part={part}
              partId={notification.notifier.uuid}
              partNumber={part.part_number}
            />{" "}
            may have been missed .
          </span>
        );
      }
    },
    missed_srp_adjustment_maps: {
      icon: "fa-cogs",
      url: notification => `/parts/${notification.notifier.uuid}`,
      message: notification => {
        const part = notification.notifier;

        return (
          <span>
            There has been no outgoing quote activity for{" "}
            <PartLink
              notificationId={notification.id}
              part={part}
              partId={part.id}
              partNumber={part.part_number}
            />
            . Please review for SRP adjustment.
          </span>
        );
      }
    },
    nap_reshop_reminder: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/parts/${notification.notifier.uuid}/incoming-quote-line-items`,
      message: notification => (
        <span>
          Neglected Parts Reminder: Please shop{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier}
            partNumber={notification.notifier.part_number}
            partId={notification.notifier.uuid}
          />
          .
        </span>
      )
    },
    out_of_stock_check_repairs: {
      icon: "fa-cogs",
      url: notification => `/parts/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          We are sold out of{" "}
          <PartLink
            part={notification.notifier}
            partNumber={notification.notifier.part_number}
            partId={notification.notifier.uuid}
          />
          . Please review Internal Outright/s for possible return.
        </span>
      )
    },
    out_of_stock_check_working_to_save: {
      icon: "fa-cogs",
      url: notification => `/parts/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            part={notification.notifier}
            partNumber={notification.notifier.part_number}
            partId={notification.notifier.uuid}
          />{" "}
          is sold out. Please review for stock replenishment.
        </span>
      )
    },
    purchase_consideration: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/sales-orders/${notification.details.sales_order_uuid ||
          notification.details.sales_order_id}`,
      message: notification => (
        <span>
          Please shop{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier}
            partNumber={notification.notifier.part_number}
            partId={notification.notifier.uuid}
          />{" "}
          for {notification.details.store_location} store.
        </span>
      )
    },
    srp_market_warning_notification: {
      icon: "fa-cogs",
      url: notification => `/parts/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier}
            partNumber={notification.notifier.part_number}
            partId={notification.notifier.uuid}
          />{" "}
          had an incoming quote entered for less than your SRP.
        </span>
      )
    }
  },
  PurchaseOrder: {
    change_do_not_ship_reminder: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} is awaiting completed Ship To
          instructions.
        </span>
      )
    },
    consignment_created: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} created associated with{" "}
          {_.get(notification, "details.company.name")}{" "}
          {_.get(notification, "details.consignment_type", "consignment")}{" "}
          {_.get(notification, "details.consignment_reference")}.
        </span>
      )
    },
    ema_with_no_consignor_percentage_notification_sold: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            displayText={_.get(notification, "details.sales_order.code")}
            id={_.get(notification, "details.sales_order.id")}
          />{" "}
          for an EMA has triggered a $0 PO to be created. Please adjust the PO
          Price and send the Purchase Order to{" "}
          <CompanyLink company={_.get(notification, "details.consignor")} />.
        </span>
      )
    },
    denied_vendor_status: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} placed and {notification.details.company}{" "}
          has a denied vendor status. Please approve trace and reevaluate vendor
          status.
        </span>
      )
    },
    expect_paperwork_for_vendor: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} created to an unapproved vendor. Please
          review vendor status and trace.
        </span>
      )
    },
    restricted_entity: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Purchase Order flagged a name possibly matching entity from Gov&apos;t
          Screening List: {restrictedEntityToSentence(notification.details)}.
        </span>
      )
    },
    must_receive_virtually: {
      icon: "fa-shopping-cart",
      url: () => `/dashboard/virtual-receiving`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            id={notification.notifier.uuid}
            displayText={notification.notifier.code}
            path="purchase-orders"
          />{" "}
          requires virtual receiving.
        </span>
      )
    },
    must_receive_virtually_updated: {
      icon: "fa-shopping-cart",
      url: () => `/dashboard/virtual-receiving`,
      message: notification => (
        <span>
          {notification.notifier.code} updated and requires virtual receiving.
        </span>
      )
    },
    purchase_order_cancelled: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.details && notification.details.buying_store ? (
            <Fragment>{notification.details.buying_store} </Fragment>
          ) : (
            ""
          )}
          {notification.notifier.code} cancelled by{" "}
          {notification.created_by.full_name}.
        </span>
      )
    },
    purchase_order_quantity_changed: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
            partNumber={notification.details.part_number}
            partId={notification.details.part_id}
          />{" "}
          from{" "}
          {notification.details && notification.details.buying_store ? (
            <Fragment>{notification.details.buying_store} </Fragment>
          ) : (
            ""
          )}
          {notification.notifier.code} to {notification.details.company_name}{" "}
          changed from QTY {notification.details.previous_value} to QTY{" "}
          {notification.details.current_value} by{" "}
          {notification.details.updated_by || notification.created_by.full_name}
          .
        </span>
      )
    },
    purchase_order_cost_changed: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
            partNumber={notification.details.part_number}
            partId={notification.details.part_id}
          />{" "}
          from{" "}
          {notification.details && notification.details.buying_store ? (
            <Fragment>{notification.details.buying_store} </Fragment>
          ) : (
            ""
          )}
          {notification.notifier.code} to {notification.details.company_name}{" "}
          changed from {dollars(notification.details.previous_value)} to{" "}
          {dollars(notification.details.current_value)} by{" "}
          {notification.details.updated_by}.
        </span>
      )
    },
    purchase_order_with_demerits: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <strong>{notification.notifier.code}</strong> has been issued to a
          vendor with 4 or more active demerits in the last 24 months. The order
          will require QC Approval, before allowing{" "}
          {notification.created_by.full_name} to send PO.
        </span>
      )
    },
    purchase_order_shipping_type_change: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => {
        const reason = _.get(notification, "details.reason");
        return (
          <span>
            <ItemLink
              notificationId={notification.id}
              path="purchase-orders"
              id={notification.notifier.uuid}
              displayText={notification.notifier.code}
            />{" "}
            will require {reason === "requires_8130_3" && "8130-3"}
            {reason === "requires_trace_documentation" && "trace"}
            {reason === "requires_weight_and_dims" && "weight/dim"}
            {reason === "requires_picture" && "picture"} approval prior to
            shipping.
          </span>
        );
      }
    },
    received_and_not_approved_vendor: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} received without vendor status approval.
          Please review.
        </span>
      )
    },
    trace_hold: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} to{" "}
          {_.get(notification, "details.company.name")} requires trace approval.
        </span>
      )
    },
    trace_hold_approved: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Trace for {notification.notifier.code} approved. Please send purchase
          order to {_.get(notification, "details.company_name")}.
        </span>
      )
    },
    trace_hold_rejected: {
      icon: "fa-shopping-cart",
      url: notification => `/purchase-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Trace for <strong>{notification.notifier.code}</strong> has been
          denied by {_.get(notification, "details.rejected_by")} and order to{" "}
          {_.get(notification, "details.company_name")} should be rejected.
        </span>
      )
    }
  },
  PurchaseOrderLineItem: {
    cancelled_consignment: {
      icon: "fa-shopping-cart",
      url: notification => `/parts/${notification.notifier.part_id}`,
      message: notification => (
        <span>
          Consignment {notification.notifier.purchase_order.code} to{" "}
          {_.get(notification, "details.company")} cancelled. Please review.
        </span>
      )
    },
    received_reserved_inventory_item: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/purchase-orders/${notification.notifier.purchase_order_id}`,
      message: notification => (
        <span>
          Reserved{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          received. Please create sales order.
        </span>
      )
    },
    requires_approval: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/purchase-orders/${notification.notifier.purchase_order.uuid}`,
      message: notification => (
        <span>
          <PartLink
            part={notification.details.part}
            partNumber={notification.details.part_number}
            partId={notification.details.part_id}
          />{" "}
          SN-{notification.notifier.serial_number} purchased by{" "}
          {getFirstNameLastInitial(notification.details.buyer)} was received but
          still requires PO approval.
        </span>
      )
    },
    partially_received_reserved_inventory_item: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/purchase-orders/${notification.notifier.purchase_order_id}`,
      message: notification => (
        <span>
          {notification.notifier.purchase_order.code}, reserved for you, was
          partially received as expected.
        </span>
      )
    },
    difference_in_received_inventory_item: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/purchase-orders/${notification.notifier.purchase_order_id}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          received with these differences: {getChangedDetails(notification)}.
        </span>
      )
    },
    difference_in_partially_received_inventory_item: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/purchase-orders/${notification.notifier.purchase_order_id}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          partially received with these differences:
          <br />
          {getChangedDetails(notification)}.
        </span>
      )
    },
    expected_date_updated: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/purchase-orders/${notification.notifier.purchase_order_id}`,
      message: notification => {
        const code = notification.details.purchase_order_code;
        const who = notification.created_by.full_name;
        const date = notification.details.new_date;

        return (
          <span>
            Expected Date for {code} has been updated by {who}
            {date ? <React.Fragment> to {date}.</React.Fragment> : "."}
          </span>
        );
      }
    },
    must_split_received_inventory_in_order_to_reserve_for_repair: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/purchase-orders/${notification.notifier.purchase_order_id}`,
      message: notification => (
        <span>
          <strong>
            <PartLink
              notificationId={notification.id}
              part={notification.notifier.part}
              partNumber={notification.notifier.part.part_number}
              partId={notification.notifier.part_id}
            />{" "}
            from {notification.notifier.purchase_order.code}
          </strong>
          , reserved for repair, was received with more than one item. Please
          split inventory to reserve for repair.
        </span>
      )
    },
    on_order_item_received: {
      icon: "fa-dollar",
      url: notification =>
        `/dashboard/shipping?filter=${_.get(
          notification,
          "details.sales_order_code"
        )}`,
      message: notification => (
        <span>
          {notification.notifier.purchase_order.code} on order for{" "}
          <ItemLink
            notificationId={notification.id}
            displayText={_.get(notification, "details.sales_order_code")}
            path="sales-orders"
            id={_.get(notification, "details.sales_order_id")}
          />{" "}
          received. Please prepare for shipment.
        </span>
      )
    },
    purchase_order_item_condition_mismatch: {
      icon: "fa-shopping-cart",
      url: notification => {
        if (!notification.details) {
          return `/purchase-orders/${notification.notifier.purchase_order_id}`;
        }
        return `/inventories/${notification.details.inventory.id}`;
      },
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          received is not matching SRP condition.
        </span>
      )
    },
    purchase_order_line_item_cancelled: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/purchase-orders/${notification.notifier.purchase_order_id}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          {notification.notifier.serial_number &&
            ` SN-
          ${notification.notifier.serial_number}`}{" "}
          from {_.get(notification, "details.purchase_order.buying_store")}{" "}
          {_.get(notification, "details.purchase_order.code")}{" "}
          {_.get(notification, "details.purchase_order.company.name") &&
            `to ${_.get(
              notification,
              "details.purchase_order.company.name"
            )}`}{" "}
          has been cancelled by {notification.created_by.full_name}.
        </span>
      )
    },
    first_time_part_number_used: {
      icon: "fa-cogs",
      url: notification => `/parts/${notification.notifier.part_id}`,
      message: notification => (
        <span>
          Please review{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          purchased for the first time on{" "}
          <ItemLink
            notificationId={notification.id}
            displayText={notification.notifier.purchase_order.code}
            id={notification.notifier.purchase_order_id}
            path="purchase-orders"
          />{" "}
          for pertinent logistics data.
        </span>
      )
    },
    purchase_order_dim_weight: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/purchase-orders/${notification.notifier.purchase_order.id}`,
      message: notification => (
        <span>
          Please review {notification.notifier.purchase_order.code} for possible{" "}
          weights and dims factor.
        </span>
      )
    }
  },
  IncomingQuote: {
    customer_replied_to_iq: {
      icon: "fa-quote-left",
      url: notification => `/incoming-quotes/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.company.name} has replied to RFQ{" "}
          {notification.notifier.code}. Please Review.
        </span>
      )
    },
    import_failed: {
      icon: "fa-quote-left",
      url: notification => `/incoming-quotes/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Incoming Quote Failed.{" "}
          {_.get(notification, "details.processing_error")
            ? "Processing error please check file type."
            : ""}
          .
        </span>
      )
    },
    import_succeeded: {
      icon: "fa-quote-left",
      url: notification => `/incoming-quotes/${notification.notifier.uuid}`,
      message: () => <span>Incoming Quote Import Succeeded.</span>
    },
    incoming_quote_awaiting_reply: {
      icon: "fa-quote-left",
      url: notification => `/incoming-quotes/${notification.notifier.uuid}`,
      message: notification => {
        const parts = _.get(notification, "details.parts");
        let partsArray =
          parts &&
          parts.map((p, index) => (
            <Fragment>
              <PartLink notificationId={notification.id} part={p} />
              {index === parts.length - 1 ? "" : ", "}
            </Fragment>
          ));

        if ((partsArray || []).length > 1) {
          const lastElem = partsArray.pop();
          partsArray = partsArray.concat(["and ", lastElem]);
        }
        const companyText = notification.notifier.company ? (
          <Fragment>
            &nbsp;from{" "}
            <ItemLink
              notificationId={notification.id}
              id={notification.notifier.company.uuid}
              path="companies"
              displayText={notification.notifier.company.name}
            />{" "}
          </Fragment>
        ) : (
          ""
        );

        return (
          <span>
            {notification.notifier.code}
            {companyText}
            {[" for "].concat(partsArray)} still &apos;Awaiting Reply&apos;.
          </span>
        );
      }
    },
    restricted_entity: {
      icon: "fa-quote-left",
      url: notification => `/incoming-quotes/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Warning: Similar entity name in US Gov&apos;t Screening List:{" "}
          {restrictedEntityToSentence(notification.details)}.
        </span>
      )
    }
  },
  ManagedRepairOrder: {
    managed_repair_ready_to_invoice: {
      icon: "fa-recycle",
      url: notification =>
        `/managed-repair-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Managed Repair {notification.notifier.code} is ready to be invoiced to{" "}
          {_.get(notification, "details.managed_repair.company.name")}.
        </span>
      )
    }
  },
  ReceivingRecord: {
    discrepancy: {
      icon: "fa-truck",
      url: notification => `/receiving/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Discrepancy created for receiving record {notification.notifier.code}.
        </span>
      )
    },
    validate_and_enter_tagged_by: {
      icon: "fa-truck",
      url: notification =>
        `/inventories/${notification.notifier.inventories[0].id}`,
      message: notification => (
        <span>
          Please validate and enter Tagged By on recently received unit{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          / SN-{notification.notifier.serial_number}.
        </span>
      )
    },
    verify_repair_station_approval_status: {
      icon: "fa-truck",
      url: notification =>
        `/inventories/${notification.notifier.inventories[0].id}`,
      message: notification => {
        const company = _.get(
          notification,
          "details.tagged_by",
          _.get(notification, "company.name")
        );
        return (
          <span>
            <PartLink part={notification.notifier.part} /> received with
            approval certs by&nbsp;
            {company}. Please verify approval status for {company}.
          </span>
        );
      }
    },
    core_return_exchange_check_warranty: {
      icon: "fa-truck",
      url: notification =>
        `/inventories/${_.get(notification, "details.inventory_id")}`,
      message: notification => (
        <span>
          Same{" "}
          <InventoryLink
            notificationId={notification.id}
            id={notification.notifier.uuid}
            serialNumber={notification.notifier.serial_number}
          />{" "}
          received against exchange{" "}
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            id={notification.details.sales_order_id}
            displayText={notification.details.sales_order_code}
          />
          . Please evaluate.
        </span>
      )
    },
    tagged_by_mismatch_on_purchase_order_line_item_receiving_record: {
      icon: "fa-truck",
      url: notification =>
        `/inventories/${_.get(notification, "details.inventory_id")}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          <InventoryLink
            notificationId={notification.id}
            id={notification.notifier.uuid}
            serialNumber={notification.notifier.serial_number}
          />{" "}
          received tagged by{" "}
          {_.get(notification, "details.notifier.tagged_by.name")} instead of
          expected tagged by{" "}
          {_.get(
            notification,
            "details.reference_record_line_item.tagged_by.name"
          )}
          . Please consider for QC Hold.
        </span>
      )
    },
    process_quality_control_documentation: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${_.get(notification, "details.sales_order.id")}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          arrived into {notification.details.inventory.store_location} stock{" "}
          today tied to {notification.details.sales_order.code}. Please prepare{" "}
          QC Documents.
        </span>
      )
    }
  },
  RepairOrderLineItem: {
    repair_order_line_item_has_been_rejected: {
      icon: "fa-wrench",
      url: notification =>
        `/repair-orders/${notification.notifier.repair_order_id}`,
      message: notification => (
        <span>
          <PartLink
            part={notification.details.part}
            partNumber={notification.details.part.part_number}
            partId={notification.details.id}
          />{" "}
          from {notification.details.repair_order_code} has been rejected at{" "}
          {notification.details.company_name}. Please Review.
        </span>
      )
    },
    exchange_core_return_requote: {
      icon: "fa-wrench",
      url: notification => `/repair-orders/${notification.details.ro_id}`,
      message: notification => {
        const details = notification.details;
        let changeMessage = "";

        if (details.labor_cost_change) {
          changeMessage += `labor cost from $${details.labor_cost_change
            .labor_original} to $${details.labor_cost_change.labor_new}`;
        }

        if (details.part_cost_change) {
          if (changeMessage.length > 0) changeMessage += " and ";
          changeMessage += `part cost from $${details.part_cost_change
            .part_original} to $${details.part_cost_change.part_new}`;
        }

        if (details.outside_labor_cost_change) {
          if (changeMessage.length > 0) changeMessage += " and ";
          changeMessage += `outside labor cost from $${details
            .outside_labor_cost_change.outside_labor_original} to $${details
            .outside_labor_cost_change.outside_labor_new}`;
        }

        return (
          <span>
            Please review and process possible requote for {changeMessage} for{" "}
            {notification.notifier.repair_order_code} for{" "}
            <ItemLink
              notificationId={notification.id}
              id={notification.details.so_id}
              path="sales-orders"
              displayText={notification.details.sales_order_code}
            />.
          </span>
        );
      }
    },
    customer_rma_cancelled: {
      icon: "fa-wrench",
      url: notification =>
        `/repair-orders/${notification.notifier.repair_order_uuid}`,
      message: notification => (
        <span>
          <strong>{notification.notifier.repair_order_code}</strong> for{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          customer RMA cancelled. Please review.
        </span>
      )
    },
    process_internal_outright: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.details.sales_order.id}`,
      message: notification => (
        <span>
          Please process internal outright for{" "}
          {notification.details.sales_order.code}.
        </span>
      )
    },
    repair_evaluation_received_for_international_repair: {
      icon: "fa-wrench",
      url: notification =>
        `/repair-orders/${notification.notifier.repair_order_uuid}`,
      message: notification => (
        <span>
          {notification.notifier.repair_order_code} repair evaluation received
          for{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />
          . Please review shipping instructions and applicable import
          declaration.
        </span>
      )
    },
    stale_expected_date: {
      icon: "fa-wrench",
      url: notification =>
        `/repair-orders/${notification.notifier.repair_order_uuid}`,
      message: notification => (
        <span>
          Expected Date for {notification.notifier.repair_order_code}{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          has expired. Please update expected date.
        </span>
      )
    },
    exceeds_average_repair_fee: {
      icon: "fa-wrench",
      url: notification =>
        `repair-orders/${notification.notifier.repair_order_id}`,
      message: notification => (
        <span>
          Original Eval for the flat exchange{" "}
          {notification.details.sales_order_code} exceeded the Avg Repair Fee
          (NTX) by {dollars(notification.details.exceeded_repair_by)}. Please
          review and invoice customer, if applicable.
        </span>
      )
    },
    repair_line_item_rejected: {
      icon: "fa-wrench",
      url: notification =>
        `/repair-orders/${notification.notifier.repair_order_id}`,
      message: notification => (
        <span>
          Original Evaluation is available for{" "}
          {notification.notifier.repair_order_code} and is eligible to go{" "}
          outright. Please review {notification.notifier.repair_order_code} to{" "}
          determine next steps.
        </span>
      )
    },
    partial_credit_notification: {
      icon: "fa-wrench",
      url: notification => `/inventories/${notification.notifier.inventory_id}`,
      message: notification => (
        <span>
          Please review{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "notifier.part")}
            partNumber={_.get(notification, "notifier.part_number")}
            partId={_.get(notification, "notifier.part_id")}
          />
          {" / "}
          <InventoryLink
            notificationId={notification.id}
            id={_.get(notification, "notifier.inventory_id")}
            serialNumber={_.get(notification, "notifier.serial_number")}
          />{" "}
          on{" "}
          <ItemLink
            notificationId={notification.id}
            path="repair-orders"
            id={notification.notifier.repair_order_id}
            displayText={notification.notifier.repair_order_code}
          />{" "}
          for a partial credit or RMA request.
        </span>
      )
    },
    vendor_rma_requested: {
      icon: "fa-shield",
      url: notification => `/inventories/${notification.notifier.inventory_id}`,
      message: notification => {
        const partialCredit =
          _.get(notification, "details.rma_request_type") === PARTIAL_CREDIT;

        return (
          <span>
            Please request a{" "}
            {partialCredit
              ? `Partial Credit of ${dollars(
                  _.get(notification, "details.partial_credit_amount")
                )}`
              : "Vendor RMA"}{" "}
            for{" "}
            <ItemLink
              notificationId={notification.id}
              path="repair-orders"
              displayText={_.get(notification, "details.repair_order.code")}
              id={_.get(notification, "details.repair_order.id")}
            />
            .
          </span>
        );
      }
    },
    repair_order_item_condition_mismatch: {
      icon: "fa-wrench",
      url: notification => `/inventories/${notification.notifier.inventory_id}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          received is not matching SRP condition.
        </span>
      )
    },
    vendor_rma_cancelled: {
      icon: "fa-shield",
      url: notification =>
        `repair-orders/${notification.notifier.repair_order_id}`,
      message: notification => (
        <span>
          {notification.notifier.repair_order_code} for{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.notifier.part}
            partNumber={notification.notifier.part.part_number}
            partId={notification.notifier.part_id}
          />{" "}
          vendor RMA cancelled. Please review.
        </span>
      )
    }
  },
  RepairOrder: {
    core_repair_order_awaiting_approval: {
      icon: "fa-dollar",
      url: notification => `repair-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.part")}
          />{" "}
          core return received for{" "}
          <ItemLink
            notificationId={notification.id}
            displayText={notification.details.sales_order.code}
            path="sales-orders"
            id={notification.details.sales_order.uuid}
          />. Repair Order created to {notification.details.company.name}.
          Please review and approve.
        </span>
      )
    },
    import_failed: {
      icon: "fa-wrench",
      url: notification => `/repair-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Expected Components Import Failed{" "}
          {_.get(notification, "details.processing_error")
            ? "Processing error please check file type."
            : ""}
          .
        </span>
      )
    },
    import_succeeded: {
      icon: "fa-wrench",
      url: notification => `/repair-orders/${notification.notifier.uuid}`,
      message: () => <span>Expected Components Import Succeeded.</span>
    },
    repair_order_awaiting_approval: {
      icon: "fa-wrench",
      url: notification => `/repair-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} requires approval. Please review.
        </span>
      )
    },
    repair_order_with_demerits: {
      icon: "fa-wrench",
      url: notification => `/repair-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} has been issued to a vendor with 4 or
          more active demerits in the last 24 months. The order will require QC
          Approval, before allowing {notification.created_by.full_name} to send
          RO.
        </span>
      )
    },
    still_awaiting_evaluation: {
      icon: "fa-wrench",
      url: notification => `/repair-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Evaluation Received 21 days ago for {notification.notifier.code} and
          no status change. Please review.
        </span>
      )
    }
  },
  Rma: {
    charge_no_fault_found_fee: {
      icon: "fa-cog",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          No fault found for warranty {" "}
          <ItemLink
            notificationId={notification.id}
            path="rmas"
            displayText={notification.notifier.code}
            id={notification.notifier.uuid}
          />
          {". "}
          Please charge {notification.details.company.name} with no fault found
          fee of {dollars(notification.notifier.no_fault_found_fee)}.
        </span>
      )
    },
    created_customer_rma: {
      icon: "fa-cog",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Customer{" "}
          <ItemLink
            notificationId={notification.id}
            path="rmas"
            displayText={notification.notifier.code}
            id={notification.notifier.uuid}
          />{" "}
          created for{" "}
          <ItemLink
            notificationId={notification.id}
            displayText={notification.details.sales_order.code}
            id={notification.details.sales_order.id}
            path="sales-orders"
          />
          .
        </span>
      )
    },
    customer_rma_received: {
      icon: "fa-cog",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            partId={_.get(notification, "details.part_id")}
            partNumber={_.get(notification, "details.part_number")}
          />{" "}
          received for customer{" "}
          <ItemLink
            notificationId={notification.id}
            path="rmas"
            displayText={notification.notifier.code}
            id={notification.notifier.uuid}
          />
          .
        </span>
      )
    },
    issue_credit: {
      icon: "fa-cog",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Customer {notification.notifier.code} received. Please issue credit.
        </span>
      )
    },
    issue_restocking_fee: {
      icon: "fa-cog",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Customer {notification.notifier.code} received. Please issue credit
          and charge restocking fee.
        </span>
      )
    },
    customer_rma_received_create_warranty_consideration_repair_orders: {
      icon: "fa-cog",
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            displayText={notification.notifier.code}
            path="rmas"
            id={notification.notifier.uuid}
          />{" "}
          with a warranty consideration received. Please create the RO (if
          applicable).
        </span>
      ),
      url: notification =>
        `/inventories/${notification.notifier.sales_item.inventory_id}`
    },
    process_warranty_charges_for_customer_rma: {
      icon: "fa-cog",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Please process warranty charges for customer{" "}
          {notification.notifier.code}.
        </span>
      )
    },
    process_credit_warranty_charges: {
      icon: "fa-cog",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Customer {notification.notifier.code} is eligible to process credit
          and warranty charges. Please review.
        </span>
      )
    },
    process_credit_for_customer_rma: {
      icon: "fa-cog",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Customer {notification.notifier.code} is eligible to process credit.
          Please review.
        </span>
      )
    },
    vendor_rma_manually_cancelled: {
      icon: "fa-shield",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Vendor {notification.notifier.code} cancelled for{" "}
          <ItemLink
            notificationId={notification.id}
            path="purchase-orders"
            id={notification.notifier.reference_record_id}
            displayText={notification.notifier.reference_record_code}
          />
          . Please review.
        </span>
      )
    },
    vendor_rma_number_received: {
      icon: "fa-shield",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Vendor {notification.notifier.code} received for{" "}
          <ItemLink
            notificationId={notification.id}
            path="purchase-orders"
            id={notification.notifier.reference_record_id}
            displayText={notification.notifier.reference_record_code}
          />
          . Please review.
        </span>
      )
    },
    vendor_rma_partial_credit_requested: {
      icon: "fa-shield",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Vendor {notification.notifier.code} partial credit accepted for{" "}
          <ItemLink
            notificationId={notification.id}
            path="purchase-orders"
            id={notification.notifier.reference_record_id}
            displayText={notification.notifier.reference_record_code}
          />
          . Please review.
        </span>
      )
    },
    vendor_rma_partial_credit_accepted_repair_notification: {
      icon: "fa-shield",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            path="repair-orders"
            id={notification.details.repair_order.uuid}
            displayText={notification.details.repair_order.code}
          />{" "}
          for{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.part")}
          />{" "}
          Vendor {notification.notifier.code} partial credit accepted. Please
          review.
        </span>
      )
    },
    vendor_rma_partial_credit_accepted: {
      icon: "fa-shield",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Vendor {notification.notifier.code} partial credit accepted for{" "}
          <ItemLink
            notificationId={notification.id}
            path="purchase-orders"
            id={notification.notifier.reference_record_id}
            displayText={notification.notifier.reference_record_code}
          />
          . Please review.
        </span>
      )
    },
    vendor_rma_partial_credit_denied: {
      icon: "fa-shield",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Vendor {notification.notifier.code} partial credit denied for{" "}
          <ItemLink
            notificationId={notification.id}
            path="purchase-orders"
            id={notification.notifier.reference_record_id}
            displayText={notification.notifier.reference_record_code}
          />
          . Please review.
        </span>
      )
    },
    vendor_rma_partial_credit_denied_repair_notification: {
      icon: "fa-shield",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            path="repair-orders"
            id={notification.details.repair_order.uuid}
            displayText={notification.details.repair_order.code}
          />{" "}
          for{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.part")}
          />{" "}
          Vendor {notification.notifier.code} partial credit denied. Please
          review.
        </span>
      )
    },
    vendor_rma_credit_denied: {
      icon: "fa-shield",
      url: notification => `/rmas/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Vendor {notification.notifier.code} denied for{" "}
          <ItemLink
            notificationId={notification.id}
            path="purchase-orders"
            id={notification.notifier.reference_record_id}
            displayText={notification.notifier.reference_record_code}
          />
          . Please review.
        </span>
      )
    }
  },
  SalesOrder: {
    accounting_hold_released: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} has been released from accounting hold.
        </span>
      )
    },
    awb_bol_added_to_brokered_exchange: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          AWB/BOL has been added to brokered {notification.notifier.code}.
          Please invoice.
        </span>
      )
    },
    brokered_sale_cancellation_notification_for_seller: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          SO-{notification.details.sales_order.id} to{" "}
          {notification.details.sales_order.company.name} for{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.part")}
          />{" "}
          was part of a brokered sale and has been cancelled by{" "}
          {notification.details.cancelled_by.full_name}. Please update{" "}
          {notification.details.sales_order.contact.name} and cancel or recreate
          sale with new unit.
        </span>
      )
    },
    brokered_sale_cancellation_notification_for_accounting: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          SO-{notification.details.sales_order.id} to{" "}
          {notification.details.sales_order.company.name} for{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.part")}
            partId={_.get(notification, "details.part.id")}
            partNumber={_.get(notification, "details.part.part_number")}
          />{" "}
          was part of a brokered sale and has been cancelled by{" "}
          {notification.details.cancelled_by.full_name}. Please update your
          accounting records.
        </span>
      )
    },
    commercial_invoice_attached: {
      icon: "fa-dollar",
      url: notification => `sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Commercial invoice attached to {notification.notifier.code}. Please
          review.
        </span>
      )
    },
    contingent_on_passing_rejected: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Evaluation for{" "}
          <ItemLink
            notificationId={notification.id}
            id={notification.details.repair_order.uuid}
            path="repair-orders"
            displayText={notification.details.repair_order.code}
          />{" "}
          has been Rejected for {notification.notifier.code}. Please review and
          cancel Sales Order if necessary.
        </span>
      )
    },
    credit_card_form_attached: {
      icon: "fa-dollar",
      url: notification => `sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Credit card form attached to {notification.notifier.code}. Please
          review.
        </span>
      )
    },
    customer_discreet_paperwork_available: {
      icon: "fa-dollar",
      url: notification =>
        `/dashboard/shipping?filter=${notification.notifier.code}&page=1`,
      message: notification => (
        <span>
          Discreet paperwork for{" "}
          <ItemLink
            notificationId={notification.id}
            displayText={notification.notifier.code}
            path="sales-orders"
            id={notification.notifier.uuid}
          />{" "}
          uploaded. Please attach paperwork and complete order.
        </span>
      )
    },
    accounting_has_approved_and_released_so: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} is released from accounting hold. Please
          review.
        </span>
      )
    },
    customer_expects_paperwork: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} requires QC documents approval prior to{" "}
          shipment.
        </span>
      )
    },
    contact_customer_to_provide_weight_and_dims: {
      icon: "fa-dollar",
      url: notification =>
        `/dashboard/shipping?filter=${notification.notifier.code}&page=1`,
      message: notification => (
        <span>
          Contact customer to provide weight and dims for{" "}
          <ItemLink
            notificationId={notification.id}
            displayText={notification.notifier.code}
            path="sales-orders"
            id={notification.notifier.uuid}
          />
          .
        </span>
      )
    },
    first_sales_order_to_customer: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            displayText={notification.notifier.code}
            path="sales-orders"
            id={notification.notifier.uuid}
          />{" "}
          has been created for a new customer:{" "}
          {notification.notifier.company_name}.
        </span>
      )
    },
    new_sales_order_with_line_item_on_qc_hold: {
      icon: "fa-dollar",
      url: notification => `/inventories/${notification.details.inventory_id}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            id={notification.notifier.uuid}
            displayText={notification.notifier.code}
          />{" "}
          is on QC Hold due to expiration for{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.part")}
            partNumber={_.get(notification, "details.part_number")}
            partId={_.get(notification, "details.part_id")}
          />{" "}
          SN-{_.get(notification, "details.serial_number")}. Please review.
        </span>
      )
    },
    update_shipping_from_tbd: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} is still missing shipping information.{" "}
          Please follow up with your customer.
        </span>
      )
    },
    request_delivery_method_from_wh_team: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Email customer to provide Shipping and Carrier instructions for{" "}
          {notification.notifier.code}.
        </span>
      )
    },
    restricted_address: {
      icon: "fa-quote-left",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Sales Order flagged an address possibly matching entity from
          Gov&apos;t Screening List:{" "}
          {restrictedAddressesToSentence(notification.details)}. Pre-Approval
          Required.
        </span>
      )
    },
    restricted_entity: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Sales Order flagged a name possibly matching entity from Gov&apos;t
          Screening List: {restrictedEntityToSentence(notification.details)}.
          Pre-Approval Required.
        </span>
      )
    },
    discreet_shipping_selected: {
      icon: "fa-dollar",
      message: notification => (
        <span>
          {notification.notifier.code} for {notification.details.company.name}{" "}
          updated to Discreet Drop-Shipment.
        </span>
      ),
      url: notification => `/sales-orders/${notification.notifier.uuid}`
    },
    discreet_shipping_unselected: {
      icon: "fa-dollar",
      message: notification => (
        <span>
          {notification.notifier.code} for {notification.details.company.name}{" "}
          is no longer Discreet Drop-Shipment.
        </span>
      ),
      url: notification => `/sales-orders/${notification.notifier.uuid}`
    },
    export_compliance: {
      icon: "fa-building",
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            id={notification.notifier.uuid}
            displayText={notification.notifier.code}
            path="sales-orders"
          />{" "}
          for{" "}
          <ItemLink
            notificationId={notification.id}
            id={notification.notifier.company_id}
            path="companies"
            displayText={notification.notifier.company_name}
          />{" "}
          is not Export Compliant.
        </span>
      ),
      url: notification => `/sales-orders/${notification.notifier.uuid}`
    },
    export_compliance_hold_added: {
      icon: "fa-dollar",
      message: notification => (
        <span>{notification.notifier.code} is on export compliance hold.</span>
      ),
      url: notification => `/sales-orders/${notification.notifier.uuid}`
    },
    overdue_core_return: {
      icon: "fa-dollar",
      message: notification => (
        <span>
          Core Return for Exchange {notification.notifier.code} is overdue.
          Please bill {notification.details.company} an additional exchange fee.
        </span>
      ),
      url: notification => `/sales-orders/${notification.notifier.uuid}`
    },
    sales_order_created: {
      icon: "fa-dollar",
      message: notification => (
        <span>
          Congrats!{" "}
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            displayText={notification.notifier.code}
            id={notification.notifier.uuid}
          />{" "}
          created.
        </span>
      ),
      url: notification =>
        `/reports/commissions?filter=${notification.notifier.code}`
    },
    sales_order_cancelled: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            displayText={notification.notifier.code}
            id={notification.notifier.uuid}
          />{" "}
          cancelled.
        </span>
      )
    },
    sales_order_shipping_info_changed: {
      icon: "fa-dollar",
      message: notification => (
        <span>
          Shipping details changed for{" "}
          <ItemLink
            notificationId={notification.id}
            id={notification.notifier.uuid}
            path="sales-orders"
            displayText={notification.notifier.code}
          />
          .
        </span>
      ),
      url: notification =>
        `/dashboard/shipping?filter=${notification.notifier.code}`
    },
    send_packet_to_customer_within_one_hour: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} created. Please prepare QC documents for{" "}
          {notification.notifier.company_name}.
        </span>
      )
    },
    set_accounting_hold: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => {
        const message = [];
        if (_.get(notification, "details.exceeds_credit_limit")) {
          message.push("for exceeding credit limit");
        }

        if (_.get(notification, "details.repairing")) {
          message.push("due to unit is located at shop");
        }

        return (
          <span>
            {notification.notifier.code} for{" "}
            <ItemLink
              notificationId={notification.id}
              displayText={notification.notifier.company_name}
              id={notification.notifier.company_id}
              path="companies"
            />{" "}
            is on accounting hold {message.join(" and ")}. Please review.
          </span>
        );
      }
    },
    sales_order_reopened: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          {notification.notifier.code} reinstated. Please review and prepare QC{" "}
          documents, if applicable.
        </span>
      )
    },
    core_return_not_received: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          Core Return AWB # entered on{" "}
          {shortDateYYandTime(
            notification.details.core_return_awb_number_updated_at
          )}{" "}
          and core is still not received. Please follow up with Customer.
        </span>
      )
    },
    monitored_fields_changed: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          For {notification.notifier.code},{" "}
          {notification.details.fields.sentence} changed{" "}
          {notification.details.part_numbers.count > 0
            ? ` for ${notification.details.part_numbers.sentence}`
            : ""}
          .
        </span>
      )
    },
    company_name_changed: {
      icon: "fa-dollar",
      url: notification => `/sales-orders/${notification.notifier.uuid}`,
      message: notification => (
        <span>
          For {notification.notifier.code}, customer name updated from{" "}
          {notification.details.previous_name} to{" "}
          {notification.details.current_name}.
        </span>
      )
    }
  },
  Report: {
    report_processed: {
      icon: "fa-file",
      url: () => "/reports",
      message: () => <span>Report has finished processing.</span>
    }
  },
  SalesOrderLineItem: {
    eval_approved_for_ro_with_mod_to: {
      icon: "fa-wrench",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          Repair Evaluation Approved for
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.part")}
            partId={_.get(notification, "details.inventory.part_id")}
            partNumber={_.get(notification, "details.inventory.part_number")}
          />
          with a Mod To of{" "}
          <PartLink
            notificationId={notification.id}
            part={_.get(notification, "details.mod_to")}
            partId={_.get(notification, "details.mod_to.part_id")}
            partNumber={_.get(notification, "details.mod_to.part_number")}
          />
          .
        </span>
      )
    },
    exchange_core_received_returned_to_stock: {
      icon: "fa-truck",
      url: notification => `/inventories/${notification.notifier.inventory_id}`,
      message: notification => (
        <span>
          Core received for{" "}
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            displayText={notification.notifier.sales_order_code}
            id={notification.notifier.sales_order_id}
          />{" "}
          returned to stock. Please confirm exchange is closed.
        </span>
      )
    },
    inventory_on_order_for_sale_arrived: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
          />{" "}
          arrived to {notification.details.store_location} stock tied to{" "}
          {notification.notifier.sales_order_code}. Please review.
        </span>
      )
    },
    process_core_repair_charges: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          Please process core repair charges for{" "}
          <ItemLink
            notificationId={notification.id}
            path="repair-orders"
            id={notification.details.repair_order.id}
            displayText={notification.details.repair_order.code}
          />{" "}
          for {notification.notifier.sales_order_code}.
        </span>
      )
    },
    repair_line_item_exchange_gone_outright: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          Original Evaluation exceeds outright value for{" "}
          {notification.notifier.sales_order_code}. Please review.
        </span>
      )
    },
    repairs_consideration: {
      icon: "fa-wrench",
      url: notification => `/parts/${notification.details.part_id}`,
      message: notification => (
        <span>
          Please consider sending{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
            partId={notification.details.part_id}
            partNumber={notification.details.part_number}
          />{" "}
          for repair evaluation.
        </span>
      )
    },
    subsequent_exchange_created: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          {notification.details.exchange_type} exchange fee created for{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
            partId={notification.details.part_id}
            partNumber={notification.details.part_number}
          />{" "}
          <InventoryLink
            notificationId={notification.id}
            id={notification.details.inventory_id}
            serialNumber={notification.details.serial_number}
          />
          .
        </span>
      )
    },
    purchase_consideration: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          Purchase Consideration: Please consider shopping{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
            partId={notification.details.part_id}
            partNumber={notification.details.part_number}
          />{" "}
          for {notification.details.store_location} Store.
        </span>
      )
    },
    transfer_consideration: {
      icon: "fa-cogs",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          Transfer Consideration: Please consider transferring{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
            partId={notification.details.part_id}
            partNumber={notification.details.part_number}
          />{" "}
          to {notification.details.store_location} Store.
        </span>
      )
    },
    shop_return_consideration: {
      icon: "fa-wrench",
      url: notification => `/parts/${notification.details.part_id}`,
      message: notification => (
        <span>
          Please consider bringing{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
            partId={notification.details.part_id}
            partNumber={notification.details.part_number}
          />{" "}
          back from repair.
        </span>
      )
    },
    purchase_order_changed_for_sales_item: {
      icon: "fa-shopping-cart",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          <strong>
            Purchase Order {notification.details.purchase_order_code} for Sales
            Order #{notification.details.sales_order_code} for{" "}
            <PartLink
              notificationId={notification.id}
              part={notification.details.part}
              partId={notification.details.part_id}
              partNumber={notification.details.part_number}
            />{" "}
          </strong>
          , was changed with these differences:
          {getChangedDetails(notification)}.
        </span>
      )
    },
    exchange_core_received: {
      icon: "fa-truck",
      message: notification => (
        <span>
          Core received for{" "}
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            id={notification.notifier.sales_order_id}
            displayText={notification.notifier.sales_order_code}
          />
          . Please review.
        </span>
      ),
      url: notification => `/inventories/${notification.notifier.inventory_id}`
    },
    repair_order_associated_with_an_open_sales_order_received: {
      icon: "fa-dollar",
      message: notification => (
        <span>
          {notification.details.repair_order.code} on order for{" "}
          <ItemLink
            notificationId={notification.id}
            displayText={notification.notifier.sales_order_code}
            id={notification.notifier.sales_order_id}
            path="sales-orders"
          />{" "}
          received. Please prepare for shipment.
        </span>
      ),
      url: notification =>
        `/dashboard/shipping?filter=${notification.notifier.sales_order_code}`
    },
    core_received_part_mismatch: {
      icon: "fa-dollar",
      url: notification => `/inventories/${notification.notifier.inventory_id}`,
      message: notification => (
        <span>
          Core for <strong>
            SO-{notification.notifier.sales_order_code}
          </strong>{" "}
          was received with different PN as compared to the sold unit.
          <br />
          Please re-evaluate SRP Condition and SRP.
        </span>
      )
    },
    write_up_an_internal_outright_sale: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${_.get(notification, "notifier.sales_order_id")}`,
      message: notification => (
        <span>
          This exchange has been marked for an Internal Outright. Please create{" "}
          an Internal Outright Sale for this Core.{" "}
          <strong>
            <PartLink
              notificationId={notification.id}
              part={notification.details.part}
              partId={notification.details.part_id}
              partNumber={notification.details.part_number}
            />{" "}
            - SN: {_.get(notification, "details.serial_number")}
          </strong>
          .
        </span>
      )
    },
    internal_outright_sale_created: {
      icon: "fa-dollar",
      url: notification =>
        `/reports/commissions?filter=${_.get(
          notification,
          "details.sales_order.code",
          _.get(notification, "notifier.sales_order_code")
        )}`,
      message: notification => (
        <span>
          Congrats!{" "}
          <ItemLink
            notificationId={notification.id}
            path="sales-orders"
            displayText={_.get(
              notification,
              "details.sales_order.code",
              notification.notifier.sales_order_code
            )}
            id={_.get(
              notification,
              "details.sales_order.uuid",
              _.get(notification, "notifier.sales_order_id")
            )}
          />{" "}
          created!.
        </span>
      )
    },
    sales_order_line_item_cancelled: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
            partId={notification.details.part_id}
            partNumber={notification.details.part_number}
          />{" "}
          SN-{_.get(notification, "details.serial_number")} from{" "}
          {notification.notifier.sales_order_code} cancelled.
        </span>
      )
    },
    core_return_not_received: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          Core Return AWB # entered on{" "}
          {shortDateYYandTime(
            notification.details.core_return_awb_number_updated_at
          )}{" "}
          and core is still not received. Please review.
        </span>
      )
    },
    sales_order_line_item_created: {
      icon: "fa-dollar",
      url: notification =>
        `/sales-orders/${notification.notifier.sales_order_id}`,
      message: notification => (
        <span>
          For {notification.notifier.sales_order_code},{" "}
          <PartLink
            notificationId={notification.id}
            part={notification.details.part}
            partId={notification.details.part_id}
            partNumber={notification.details.part_number}
          />{" "}
          SN-{_.get(notification, "details.serial_number")} has been added.
        </span>
      )
    }
  },
  Tenant: {
    commission_paid: {
      icon: "fa-dollar",
      url: () => `/reports/commissions?status=paid`,
      message: () => (
        <span>Commission has been processed for you today. Please review.</span>
      )
    },
    import_succeeded: {
      icon: "fa-cogs",
      url: () => `/parts`,
      message: () => <span>Part Import Succeeded.</span>
    },
    import_failed: {
      icon: "fa-cogs",
      url: () => `/parts`,
      message: () => <span>Part Import Failed.</span>
    }
  }
};

export default NOTIFICATION_TYPES;



// WEBPACK FOOTER //
// ./app/modules/Notifications/notificationTypes.jsx